import React, { useEffect, useRef, useState } from 'react';
import { apiService } from '@/services/api';
import { subscribeToChannel } from '@/services/ablyRealtime';

interface OrderItem { itemName: string; quantity: number; unitPrice?: number | string | null }
interface Order { id: string; status: string; bookingId?: string | null; items: OrderItem[]; createdAt: string }

interface Props {
  userId: string;
  bookingId?: string;
  currency?: string;
  /** "Another round" — reopen the order sheet. */
  onOrderAgain?: () => void;
}

const OPEN = ['pending', 'approved', 'making', 'coming'];
const LIVE = [...OPEN, 'delivered'];
const STEP_LABELS = ['Sent', 'Preparing', 'On the way', 'Served'];
const stepOf = (s: string): number => (s === 'pending' ? 0 : s === 'approved' || s === 'making' ? 1 : s === 'coming' ? 2 : s === 'delivered' ? 3 : 0);
const etaOf = (s: string): number => (s === 'pending' ? 10 : s === 'approved' ? 8 : s === 'making' ? 6 : s === 'coming' ? 2 : 0);
const orderTotal = (o: Order) => (o.items || []).reduce((a, i) => a + (Number(i.unitPrice) || 0) * (i.quantity || 0), 0);

/**
 * Live order tracker for the QR home. Surfaces what's otherwise buried in the order sheet: a status
 * stepper (Sent → Preparing → On the way → Served) with an ETA, the running tab total, and — when an
 * order arrives — a ping (banner + vibrate). Turns "send and hope" into "watch it come".
 */
const OrderTracker: React.FC<Props> = ({ userId, bookingId, currency, onOrderAgain }) => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [ping, setPing] = useState<string>('');
  const prevStatus = useRef<Record<string, string>>({});
  const money = (n: number) => `${currency ? currency + ' ' : ''}${n.toFixed(2)}`;

  const load = () => {
    apiService.getServiceOrders()
      .then((r) => {
        const all = ((r as any)?.data || []) as Order[];
        const mine = bookingId ? all.filter((o) => !o.bookingId || o.bookingId === bookingId) : all;
        // Detect status transitions for the arrival ping.
        for (const o of mine) {
          const was = prevStatus.current[o.id];
          if (was && was !== o.status) {
            if (o.status === 'coming') { setPing('🚶 Your order is on the way!'); vibrate(); }
            else if (o.status === 'delivered') { setPing('✅ Served — enjoy! ✨'); vibrate(); }
          }
          prevStatus.current[o.id] = o.status;
        }
        setOrders(mine);
      })
      .catch(() => { /* non-fatal */ });
  };

  const vibrate = () => { try { (navigator as any).vibrate?.([90, 50, 90]); } catch { /* noop */ } };

  useEffect(() => {
    load();
    const iv = setInterval(load, 8000);
    let unsub = () => {};
    try {
      unsub = subscribeToChannel(`user:${userId}`, ['service_order:updated', 'service_order:new'], () => load());
    } catch { /* realtime best-effort */ }
    return () => { clearInterval(iv); try { unsub(); } catch { /* noop */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, bookingId]);

  useEffect(() => { if (!ping) return; const t = setTimeout(() => setPing(''), 6000); return () => clearTimeout(t); }, [ping]);

  const billable = orders.filter((o) => LIVE.includes(o.status));
  const runningTab = billable.reduce((a, o) => a + orderTotal(o), 0);
  // The card tracks the most recent live order not dismissed.
  const active = [...billable].filter((o) => !dismissed.has(o.id)).sort((a, b) => +new Date(b.createdAt) - +new Date(a.createdAt))[0];

  if (!active && runningTab <= 0) return null;

  return (
    <div style={wrap}>
      {ping && <div style={pingBar}>{ping}</div>}

      {active && (() => {
        const step = stepOf(active.status);
        const eta = etaOf(active.status);
        const served = active.status === 'delivered';
        return (
          <div style={{ padding: '12px 14px' }}>
            <div style={topRow}>
              <span style={title}>{served ? 'Served ✨' : eta > 0 ? `Arriving in ~${eta} min` : 'Preparing your order'}</span>
              {runningTab > 0 && <span style={tab}>Tab: {money(runningTab)}</span>}
            </div>
            <div style={itemsLine}>{(active.items || []).map((i) => `${i.quantity}× ${i.itemName}`).join(', ')}</div>
            <div style={stepper}>
              {STEP_LABELS.map((label, i) => (
                <div key={label} style={stepCol}>
                  <div style={{ ...dot, ...(i <= step ? dotOn : {}) }}>{i < step || served ? '✓' : i === step ? '•' : ''}</div>
                  <div style={{ ...stepText, ...(i <= step ? stepTextOn : {}) }}>{label}</div>
                  {i < STEP_LABELS.length - 1 && <div style={{ ...line, ...(i < step ? lineOn : {}) }} />}
                </div>
              ))}
            </div>
            {served && (
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <button type="button" style={againBtn} onClick={onOrderAgain}>🍺 Another round?</button>
                <button type="button" style={dismissBtn} onClick={() => setDismissed((d) => new Set(d).add(active.id))}>Dismiss</button>
              </div>
            )}
          </div>
        );
      })()}

      {!active && runningTab > 0 && (
        <div style={topRow2}>
          <span style={{ fontSize: '0.9rem', fontWeight: 700, color: '#0f172a' }}>Your tab so far</span>
          <span style={tab}>{money(runningTab)}</span>
        </div>
      )}
    </div>
  );
};

const wrap: React.CSSProperties = { background: '#fff', border: '1px solid #eef1f5', borderRadius: 16, marginBottom: 12, overflow: 'hidden', boxShadow: '0 2px 10px rgba(15,23,42,0.05)' };
const pingBar: React.CSSProperties = { background: 'linear-gradient(135deg,#FF6B35,#FF8A5B)', color: '#fff', fontWeight: 800, fontSize: '0.88rem', padding: '9px 14px', textAlign: 'center' };
const topRow: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 };
const topRow2: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '12px 14px' };
const title: React.CSSProperties = { fontSize: '1rem', fontWeight: 800, color: '#0f172a' };
const tab: React.CSSProperties = { background: 'rgba(255,107,53,0.12)', color: '#e0531f', fontWeight: 800, fontSize: '0.85rem', padding: '3px 12px', borderRadius: 9999, whiteSpace: 'nowrap' };
const itemsLine: React.CSSProperties = { fontSize: '0.82rem', color: '#64748b', margin: '4px 0 12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const stepper: React.CSSProperties = { display: 'flex', position: 'relative' };
const stepCol: React.CSSProperties = { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' };
const dot: React.CSSProperties = { width: 22, height: 22, borderRadius: '50%', background: '#e2e8f0', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, zIndex: 1 };
const dotOn: React.CSSProperties = { background: '#FF6B35' };
const stepText: React.CSSProperties = { fontSize: '0.68rem', fontWeight: 700, color: '#94a3b8', marginTop: 5, textAlign: 'center' };
const stepTextOn: React.CSSProperties = { color: '#0f172a' };
const line: React.CSSProperties = { position: 'absolute', top: 11, left: '50%', width: '100%', height: 3, background: '#e2e8f0', zIndex: 0 };
const lineOn: React.CSSProperties = { background: '#FF6B35' };
const againBtn: React.CSSProperties = { flex: 1, background: 'linear-gradient(135deg,#FF6B35,#FF8A5B)', color: '#fff', border: 'none', borderRadius: 12, padding: '10px', fontWeight: 800, fontSize: '0.9rem', cursor: 'pointer' };
const dismissBtn: React.CSSProperties = { background: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: 12, padding: '10px 14px', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' };

export default OrderTracker;
